import { useMemo } from 'react'
import { useTrainingGoals } from '@/lib/api'
import { WidgetWrapper } from './widget-wrapper'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Check, X } from 'lucide-react'
import { uiColors } from '@/components/charts'

function getRecentMonths(count: number): { key: string; label: string }[] {
  const months: { key: string; label: string }[] = []
  const now = new Date()

  for (let i = count - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const label = date.toLocaleDateString(undefined, { month: 'short' })
    months.push({ key, label })
  }

  return months
}

interface GoalStatus {
  name: string
  months: Map<string, boolean>
}

export function ChallengeConsistencyGrid() {
  const { data: goalsData, isLoading } = useTrainingGoals()

  const months = useMemo(() => getRecentMonths(12), [])

  // Build goal status grid from the training goals progress
  const goalStatuses: GoalStatus[] = useMemo(() => {
    if (!goalsData?.progress) return []

    const statuses: GoalStatus[] = []
    const currentMonth = new Date().toISOString().slice(0, 7)

    // Progress is keyed by sport name, then by period
    for (const [sportName, periods] of Object.entries(goalsData.progress)) {
      const monthlyProgress = periods.month
      if (!monthlyProgress) continue

      // Check if any metric has a target set in config
      const sportConfig = goalsData.config?.sports.find((s) => s.name === sportName)
      if (!sportConfig?.targets?.month) continue

      const targets = sportConfig.targets.month
      const monthsMap = new Map<string, boolean>()

      // Check distance goal
      if (targets.distance_m && targets.distance_m > 0) {
        const isComplete = monthlyProgress.distance_m >= targets.distance_m
        monthsMap.set(currentMonth, isComplete)
        statuses.push({
          name: `${sportName} distance`,
          months: new Map(monthsMap),
        })
      }

      // Check elevation goal
      if (targets.elevation_m && targets.elevation_m > 0) {
        const isComplete = monthlyProgress.elevation_m >= targets.elevation_m
        statuses.push({
          name: `${sportName} elevation`,
          months: new Map([[currentMonth, isComplete]]),
        })
      }

      // Check time goal
      if (targets.moving_time_s && targets.moving_time_s > 0) {
        const isComplete = monthlyProgress.moving_time_s >= targets.moving_time_s
        statuses.push({
          name: `${sportName} time`,
          months: new Map([[currentMonth, isComplete]]),
        })
      }
    }

    return statuses.slice(0, 5) // Limit to 5 goals for display
  }, [goalsData])

  const hasGoals = goalStatuses.length > 0

  return (
    <WidgetWrapper title="Goal Consistency" isLoading={isLoading}>
      {!hasGoals ? (
        <div className="text-sm text-muted-foreground">
          No monthly goals configured. Set up goals in Training Goals widget.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead className="py-1 text-left font-medium">Goal</TableHead>
                {months.map((m) => (
                  <TableHead
                    key={m.key}
                    className="py-1 text-center font-medium"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                  >
                    {m.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {goalStatuses.map((goal, idx) => (
                <TableRow key={idx} className="border-t border-border/50">
                  <TableCell
                    className="py-1.5 pr-2 font-medium truncate max-w-[120px]"
                    title={goal.name}
                  >
                    {goal.name}
                  </TableCell>
                  {months.map((m) => {
                    const status = goal.months.get(m.key)
                    return (
                      <TableCell key={m.key} className="py-1.5 text-center">
                        {status === undefined ? (
                          <span className="text-muted-foreground/30">—</span>
                        ) : status ? (
                          <span
                            className="inline-flex items-center justify-center w-6 h-6 rounded"
                            style={{
                              backgroundColor: `${uiColors.success}20`,
                              color: uiColors.success,
                            }}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center justify-center w-6 h-6 rounded"
                            style={{
                              backgroundColor: `${uiColors.danger}20`,
                              color: uiColors.danger,
                            }}
                          >
                            <X className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </WidgetWrapper>
  )
}
